<script setup lang="ts">
import { HelpCircle } from 'lucide-vue-next'
import type { StoreOption } from '@/types/reviewPlatforms'

const props = defineProps<{
  isOpen: boolean
  availableStores: StoreOption[]
  isSaving: boolean
}>()

const emit = defineEmits<{
  close: []
  save: [payload: { storeId: number; restaurantUuid: string }]
}>()

const { t } = useI18n()

const selectedStoreId = ref<number | null>(null)
const restaurantUuid = ref('')

watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      selectedStoreId.value = null
      restaurantUuid.value = ''
    }
  }
)

const storeOptions = computed(() => props.availableStores.map((s) => ({ value: s.id, label: s.name })))

const canSave = computed(() => selectedStoreId.value !== null && restaurantUuid.value.trim().length > 0)

function onSave() {
  if (!canSave.value) return
  emit('save', { storeId: selectedStoreId.value as number, restaurantUuid: restaurantUuid.value.trim() })
}
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    :heading-title="t('pages.reviewPlatforms.configuration.ubereats.linkTitle')"
    size="md"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4 px-6 py-4">
      <Alert class="flex items-center gap-2">
        <HelpCircle class="size-4 shrink-0" />
        <AlertDescription>{{ t('pages.reviewPlatforms.configuration.ubereats.uuidHelp') }}</AlertDescription>
      </Alert>

      <Alert class="flex items-start gap-2">
        <HelpCircle class="mt-0.5 size-4 shrink-0" />
        <AlertDescription>{{ t('pages.reviewPlatforms.configuration.ubereats.accountHelp') }}</AlertDescription>
      </Alert>

      <div class="space-y-2">
        <label class="text-sm font-medium text-foreground">{{
          t('pages.reviewPlatforms.configuration.shared.assignStore')
        }}</label>
        <ComboboxSelect
          :model-value="selectedStoreId ?? undefined"
          :options="storeOptions"
          value-key="value"
          label-key="label"
          :placeholder="t('pages.reviewPlatforms.configuration.shared.selectStore')"
          @update:model-value="(val) => (selectedStoreId = typeof val === 'number' ? val : null)"
        />
      </div>

      <div class="space-y-2">
        <label for="ubereats-uuid" class="text-sm font-medium text-foreground">{{
          t('pages.reviewPlatforms.configuration.ubereats.uuidLabel')
        }}</label>
        <Input
          id="ubereats-uuid"
          v-model="restaurantUuid"
          :placeholder="t('pages.reviewPlatforms.configuration.ubereats.uuidPlaceholder')"
          class="font-mono text-xs"
        />
      </div>

      <div class="flex items-center justify-end gap-2 pt-2">
        <Button variant="ghost" size="sm" :disabled="isSaving" @click="emit('close')">
          {{ t('pages.reviewPlatforms.configuration.shared.cancel') }}
        </Button>
        <Button variant="default-primary" size="sm" :disabled="isSaving || !canSave" @click="onSave">
          {{ t('pages.reviewPlatforms.configuration.shared.validate') }}
        </Button>
      </div>
    </div>
  </BaseModal>
</template>
